import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useRouteChangeHandler } from 'shared/hooks';

import { AUTO_SLIDE_INTERVAL, PRODUCT_IMAGES } from '../constants';
import { useAutoSlide } from '../hook/useAutoSlide';
import { usePopupVisibility } from '../hook/usePopupVisibility';

const CheckmateMallPopup = () => {
  const { t } = useTranslation();
  const { isVisible, hideForToday, hideForSession } = usePopupVisibility();
  const { currentIndex, goToNext, goToPrev } = useAutoSlide({
    totalSlides: PRODUCT_IMAGES.length,
    interval: AUTO_SLIDE_INTERVAL,
    isActive: isVisible,
  });
  const { handleRouteChange } = useRouteChangeHandler();
  const [imageKey, setImageKey] = useState(0);

  // Trigger animation when image changes
  useEffect(() => {
    setImageKey((prev) => prev + 1);
  }, [currentIndex]);

  if (!isVisible) return null;

  const handleExploreClick = () => {
    window.dataLayer?.push({ event: 'click_main', menu: 'explore', section: 'Checkmate Mall Popup' });
    handleRouteChange('/checkmate-mall');
  };

  return (
    <div className="pointer-events-none fixed bottom-0 right-4 z-50 hidden md:right-[90px] md:block">
      <div className="pointer-events-auto relative flex w-[90vw] max-w-[420px] animate-slideUpFromBottom flex-col items-center gap-[32px] overflow-hidden rounded-t-[20px] bg-gradient-to-b from-[#ffe4e4] to-[#fff8f8] pb-0 pt-[28px] shadow-[0px_4px_40px_0px_rgba(0,0,0,0.15)]">
        {/* Content Section */}
        <div className="flex w-full flex-col items-center">
          <div className="flex flex-col items-center gap-[30px]">
            {/* Header */}
            <div className="flex flex-col items-center gap-[16px] px-4">
              {/* Free Delivery Badge */}
              <div className="flex items-center justify-center rounded-bl-[8px] rounded-br-[8px] rounded-tl-[2px] rounded-tr-[2px] border-[1.4px] border-solid border-[rgba(255,45,46,0.4)] bg-[#ffe9e9] px-[8px] py-[3px]">
                <p className="text-[14px] font-semibold leading-[1.4] tracking-[-0.3px] text-[#ff2d2e]">
                  {t('common:checkmateMallPopup.freeDelivery')}
                </p>
              </div>

              {/* Title */}
              <div className="flex w-full flex-col items-center gap-[2px] text-center text-[18px] leading-[1.4] tracking-[-0.4px] text-[#222]">
                <div className="flex w-full flex-col justify-center font-normal">
                  <p>{t('common:checkmateMallPopup.title1')}</p>
                </div>
                <div className="flex flex-col justify-center font-bold">
                  <p>{t('common:checkmateMallPopup.title2')}</p>
                </div>
              </div>
            </div>

            {/* Product Slider */}
            <div className="flex items-center gap-[16px] px-2">
              {/* Left Arrow */}
              <button
                onClick={goToPrev}
                className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden transition-opacity hover:opacity-70"
                aria-label="Previous product"
              >
                <svg width="9" height="15" viewBox="0 0 11 18" fill="none" className="rotate-180">
                  <path
                    d="M1.5 1.5L9 9L1.5 16.5"
                    stroke="#3F3F3F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Product Image */}
              <button
                onClick={goToNext}
                className="h-[220px] w-[200px] shrink-0 overflow-hidden rounded-[10px] transition-transform hover:scale-[1.02]"
                aria-label="Next product"
              >
                <img
                  key={imageKey}
                  src={PRODUCT_IMAGES[currentIndex]}
                  alt={`Product ${currentIndex + 1}`}
                  className="h-full w-full animate-slideInFromRight object-cover"
                />
              </button>

              {/* Right Arrow */}
              <button
                onClick={goToNext}
                className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden transition-opacity hover:opacity-70"
                aria-label="Next product"
              >
                <svg width="9" height="15" viewBox="0 0 11 18" fill="none">
                  <path
                    d="M1.5 1.5L9 9L1.5 16.5"
                    stroke="#3F3F3F"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="flex w-full flex-col items-start">
          {/* Explore Button */}
          <button
            onClick={handleExploreClick}
            className="flex w-full items-center bg-gradient-to-r from-[#ff2d2e] to-[#ffb8b9] px-[16px] py-[14px] transition-opacity hover:opacity-90"
          >
            <div className="flex flex-1 flex-col justify-center text-center text-[17px] font-semibold leading-[1.4] tracking-[-0.4px] text-white">
              <p>{t('common:checkmateMallPopup.exploreButton')}</p>
            </div>
          </button>

          {/* Footer Actions */}
          <div className="flex w-full items-center justify-between whitespace-nowrap bg-white px-[20px] py-[14px] text-[13px] leading-[1.5] tracking-[-0.3px]">
            <button
              onClick={hideForToday}
              className="flex flex-col justify-center text-[#8e8e8e] transition-opacity hover:opacity-70"
            >
              <p>{t('common:checkmateMallPopup.hideToday')}</p>
            </button>
            <button
              onClick={hideForSession}
              className="flex flex-col justify-center text-center text-[#222] transition-opacity hover:opacity-70"
            >
              <p>{t('common:checkmateMallPopup.close')}</p>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CheckmateMallPopup;
